import { useEffect, useRef, useState, type MouseEvent } from "react";

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
};

const EMOJI_GROUPS: Array<{ title: string; emojis: string[] }> = [
  { title: "Smileys", emojis: ["😀", "😁", "😂", "🤣", "😊", "😍", "😘", "😎", "🤩", "😇", "🙂", "😉", "🤔", "😴", "😭", "😡"] },
  { title: "Gestures", emojis: ["👍", "👎", "👏", "🙏", "👌", "🤝", "🤗", "✌️", "🤞", "👆", "👇", "🙌", "💪", "🤟", "🫶", "👋"] },
  { title: "Hearts", emojis: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💖", "💯", "✨", "🔥", "⭐", "🌟", "⚡"] },
  { title: "Objects", emojis: ["📈", "📉", "💼", "💰", "💵", "💸", "📰", "📊", "🏦", "📌", "📝", "📎", "💡", "📣", "✅", "❌"] },
];

const normalizeEditorHtml = (html: string): string => {
  const cleaned = String(html || "").trim();
  if (!cleaned || cleaned === "<br>" || cleaned === "<div><br></div>" || cleaned === "<p><br></p>") {
    return "";
  }

  return cleaned;
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Write summary...",
  className = "",
}: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<Range | null>(null);
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);

  const pushHistory = (html: string) => {
    const normalized = normalizeEditorHtml(html);
    const current = historyRef.current[historyIndexRef.current];
    if (current === normalized) return;

    const nextHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
    nextHistory.push(normalized);
    historyRef.current = nextHistory;
    historyIndexRef.current = nextHistory.length - 1;
  };

  const emitEditorChange = (recordHistory = true) => {
    const normalized = normalizeEditorHtml(editorRef.current?.innerHTML || "");
    if (recordHistory) {
      pushHistory(normalized);
    }
    onChange(normalized);
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (editor.innerHTML !== value) {
      editor.innerHTML = value || "";
      const normalized = normalizeEditorHtml(value || "");
      historyRef.current = [normalized];
      historyIndexRef.current = 0;
    }
  }, [value]);

  useEffect(() => {
    if (!isEmojiOpen) return;

    const handleOutsideClick = (event: MouseEvent | globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!toolbarRef.current?.contains(target)) {
        setIsEmojiOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsEmojiOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isEmojiOpen]);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    selectionRef.current = selection.getRangeAt(0);
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    const range = selectionRef.current;
    if (!selection || !range) return;

    selection.removeAllRanges();
    selection.addRange(range);
  };

  const runCommand = (command: string, arg?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, arg);
    saveSelection();
    emitEditorChange();
  };

  const onToolbarAction = (action: () => void) => (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    action();
  };

  const insertLink = () => {
    editorRef.current?.focus();
    restoreSelection();

    const url = window.prompt("Enter link URL (https://...)");
    if (!url) return;

    const trimmed = url.trim();
    const normalized =
      /^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`;

    runCommand("createLink", normalized);
  };

  const getCurrentRange = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();

    if (!editor || !selection || selection.rangeCount === 0) {
      return null;
    }

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      return null;
    }

    return range;
  };

  const applyHeadingTwo = () => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreSelection();

    const selection = window.getSelection();
    const range = getCurrentRange();
    if (!selection || !range || range.collapsed) return;

    const extracted = range.extractContents();
    const heading = document.createElement("h2");
    heading.appendChild(extracted);

    const nextParagraph = document.createElement("p");
    nextParagraph.appendChild(document.createElement("br"));

    const fragment = document.createDocumentFragment();
    fragment.appendChild(heading);
    fragment.appendChild(nextParagraph);
    range.insertNode(fragment);

    const cursorRange = document.createRange();
    cursorRange.setStart(nextParagraph, 0);
    cursorRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(cursorRange);

    saveSelection();
    emitEditorChange();
  };

  const runUndo = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const canUndoFromHistory = historyIndexRef.current > 0;
    if (!canUndoFromHistory) return;

    historyIndexRef.current -= 1;
    const previousHtml = historyRef.current[historyIndexRef.current] || "";
    editor.innerHTML = previousHtml;

    const selection = window.getSelection();
    if (selection) {
      const cursorRange = document.createRange();
      cursorRange.selectNodeContents(editor);
      cursorRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(cursorRange);
    }

    saveSelection();
    emitEditorChange(false);
  };

  const insertEmoji = (emoji: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection) return;

    let range = getCurrentRange();
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    range.deleteContents();
    const textNode = document.createTextNode(emoji);
    range.insertNode(textNode);

    const cursorRange = document.createRange();
    cursorRange.setStartAfter(textNode);
    cursorRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(cursorRange);

    saveSelection();
    emitEditorChange();
  };

  const convertHeadingToNormal = () => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreSelection();

    const selection = window.getSelection();
    const range = getCurrentRange();
    if (!selection || !range) return;

    const headings = Array.from(editor.querySelectorAll("h2"));
    const matchedHeadings = headings.filter((heading) => {
      if (range.collapsed) {
        return heading.contains(range.startContainer);
      }

      return range.intersectsNode(heading);
    });

    if (!matchedHeadings.length) {
      runCommand("formatBlock", "<P>");
      return;
    }

    const replacementParagraphs = matchedHeadings.map((heading) => {
      const paragraph = document.createElement("p");
      paragraph.innerHTML = heading.innerHTML || "<br>";
      heading.replaceWith(paragraph);
      return paragraph;
    });

    const firstParagraph = replacementParagraphs[0];
    const cursorRange = document.createRange();
    cursorRange.selectNodeContents(firstParagraph);
    cursorRange.collapse(false);
    selection.removeAllRanges();
    selection.addRange(cursorRange);

    saveSelection();
    emitEditorChange();
  };

  return (
    <div className={`rich-editor ${className}`}>
      <div className="rich-editor-toolbar" ref={toolbarRef}>
        <button
          type="button"
          onMouseDown={onToolbarAction(convertHeadingToNormal)}
          className="rich-editor-btn"
          title="Normal"
        >
          Normal
        </button>
        <button
          type="button"
          onMouseDown={onToolbarAction(() => runCommand("bold"))}
          className="rich-editor-btn"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onMouseDown={onToolbarAction(applyHeadingTwo)}
          className="rich-editor-btn"
          title="Heading 2"
        >
          H2
        </button>
        <button type="button" onMouseDown={onToolbarAction(insertLink)} className="rich-editor-btn" title="Insert Link">
          Link
        </button>
        <button
          type="button"
          onMouseDown={onToolbarAction(runUndo)}
          className="rich-editor-btn"
          title="Undo"
        >
          Undo
        </button>
        <div className="rich-editor-emoji-wrap">
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              saveSelection();
              setIsEmojiOpen((prev) => !prev);
            }}
            className="rich-editor-btn"
            title="Insert Emoji"
            aria-label="Insert Emoji"
          >
            😊
          </button>
          {isEmojiOpen && (
            <div
              className="rich-editor-emoji-popover"
              onMouseDown={(event) => event.preventDefault()}
            >
              {EMOJI_GROUPS.map((group) => (
                <div key={group.title} className="rich-editor-emoji-group">
                  <p className="rich-editor-emoji-title">{group.title}</p>
                  <div className="rich-editor-emoji-grid">
                    {group.emojis.map((emoji) => (
                      <button
                        key={`${group.title}-${emoji}`}
                        type="button"
                        className="rich-editor-emoji-btn"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          insertEmoji(emoji);
                        }}
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="rich-editor-content"
        data-placeholder={placeholder}
        onInput={() => {
          saveSelection();
          emitEditorChange();
        }}
        onBlur={saveSelection}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
      />
    </div>
  );
};

export default RichTextEditor;
